/**
 * Conteúdo do popup de um foco (consumido pelo gerenciador de cliques do
 * app-core via `layer.get('popup')`).
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { formatInt, formatLonLat } from '../lib/format';
import { lonLatToMap } from '../map/projection';
import { formatDateTimeBrt } from '../lib/time';
import { formatDecimal, riskClass } from './format';
import { GOES_SATELLITE } from './dataset';
import { estimateMunicipio, estimateMunicipioNow, type EstimatedMunicipio } from './municipio';
import { REFERENCE_SATELLITE, useFireStore } from './store';
import { CarDoFoco, UsoDoSoloDoFoco, useTerrenoDoFoco } from '../terreno/TerrenoSection';
import { BotaoDenunciar } from '../denuncia/BotaoDenunciar';
import { Zap } from 'lucide-react';
import { fraseCurta } from '../raios/frase';
import { useRaiosDoFoco } from '../raios/useRaiosDoFoco';
import type { Fire } from './types';

export function satelliteLabel(sat: string): string {
  if (sat === GOES_SATELLITE) return 'GOES-19 (geoestacionário)';
  if (sat === REFERENCE_SATELLITE) return 'AQUA_M-T (satélite de referência)';
  return sat;
}

/**
 * Município estimado pela localização, para focos sem município (GOES-19 de
 * 10 min sem par no diário). Com os municípios em memória é imediato; senão
 * carrega (exceto com economia de dados). 'pending' = calculando.
 */
function useEstimatedMunicipio(fire: Fire): EstimatedMunicipio | null | 'pending' {
  const saveData = useFireStore((s) => s.saveData);
  const [x, y] = useMemo(() => lonLatToMap(fire.lon, fire.lat), [fire.lon, fire.lat]);
  const initial = fire.municipio ? null : estimateMunicipioNow(x, y);
  const [est, setEst] = useState<EstimatedMunicipio | null | 'pending'>(initial === undefined ? 'pending' : initial);
  useEffect(() => {
    if (fire.municipio) return setEst(null);
    const now = estimateMunicipioNow(x, y);
    if (now !== undefined) return setEst(now);
    if (saveData) return setEst(null);
    let alive = true;
    setEst('pending');
    estimateMunicipio(x, y)
      .then((r) => alive && setEst(r))
      .catch(() => alive && setEst(null));
    return () => {
      alive = false;
    };
  }, [fire.municipio, x, y, saveData]);
  return est;
}

/** O que as duas fichas (desktop e celular) calculam do foco. */
function useDadosDoFoco(fire: Fire) {
  const style = useFireStore((s) => s.style);
  const color = fire.satelite === GOES_SATELLITE ? style.colorGoes : style.colorOthers;
  const risk = fire.riscoFogo != null ? riskClass(fire.riscoFogo) : null;
  const place = fire.municipio ? `${fire.municipio}${fire.estado ? `/${fire.estado}` : ''}` : null;
  const estimated = useEstimatedMunicipio(fire);
  const saveData = useFireStore((s) => s.saveData);
  // UF para o CAR: a do INPE; senão a do município estimado (undefined = estimando).
  const uf = fire.estado ?? (estimated === 'pending' ? undefined : (estimated?.uf ?? null));
  return { color, risk, place, estimated, saveData, uf };
}

function Titulo(props: { color: string }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <span
        className="inline-block size-3 shrink-0 rounded-full"
        style={{ background: props.color, boxShadow: `0 0 8px 2px ${props.color}88` }}
        aria-hidden
      />
      <span className="text-sm font-semibold text-white">Foco de queimada</span>
    </div>
  );
}

function OutrosNoPonto(props: { stack: number }) {
  if (props.stack <= 1) return null;
  const n = props.stack - 1;
  return (
    <p className="mt-2 text-ink-300">
      +{formatInt(n)} {n === 1 ? 'outro foco' : 'outros focos'} neste ponto
    </p>
  );
}

function FonteInpe(props: { fire: Fire }) {
  return (
    <p className="mt-2 border-t border-ink-700/70 pt-1.5 text-[11px] text-ink-400">
      Fonte: INPE — Programa Queimadas
      {props.fire.source === 'goes10min' ? ' (arquivos a cada 10 min)' : ' (arquivo diário)'}
    </p>
  );
}

export function FirePopup(props: { fire: Fire; stack?: number }) {
  // Chave do ponto: a ficha é reaproveitada ao clicar noutro foco, e o estado não pode vir junto.
  return <FichaDoFoco key={`${props.fire.lat},${props.fire.lon}`} {...props} />;
}

type Aba = 'foco' | 'uso' | 'car';
/** A última aba escolhida vale para o próximo foco aberto (na sessão). */
let ultimaAba: Aba = 'foco';

/** (testes) Esquece a última aba escolhida. */
export function esquecerAbaDoFoco(): void {
  ultimaAba = 'foco';
}

/** Nome curto do satélite, para a linha do celular. */
function sateliteCurto(sat: string): string {
  return sat === GOES_SATELLITE ? 'GOES-19' : sat;
}

/**
 * Ficha do foco em abas (pedido do usuário, 28/10/2026; primeiro no celular,
 * depois também no desktop): Foco (o essencial em poucas linhas), Uso do solo
 * (o gráfico) e CAR (os imóveis). O terreno é pedido quando a ficha abre, para
 * a troca de aba ser imediata, e o destaque no mapa aparece com a ficha. Ela
 * abre abaixo do foco, presa pelo topo (`popupAbaixo`): a altura muda de aba
 * para aba e as abas ficam paradas.
 */
function FichaDoFoco(props: { fire: Fire; stack?: number }) {
  const { fire, stack = 1 } = props;
  const { color, risk, place, estimated, saveData, uf } = useDadosDoFoco(fire);
  const [aba, setAbaLocal] = useState<Aba>(ultimaAba);
  // Os contornos do CAR só aparecem no mapa com a aba CAR aberta (o círculo, sempre).
  const t = useTerrenoDoFoco(fire.lat, fire.lon, uf, saveData, aba === 'car');
  const setAba = (a: Aba) => {
    ultimaAba = a;
    setAbaLocal(a);
  };
  const nImoveis = t.estado.tipo === 'pronto' ? t.estado.dado.car.imoveis.length : null;
  // Raio perto antes do foco: a única causa natural comum de fogo na Amazônia.
  const raios = useRaiosDoFoco(fire.lat, fire.lon, fire.t);
  const id = useId();
  const abaRefs = useRef<Partial<Record<Aba, HTMLButtonElement | null>>>({});
  const teclar = (e: React.KeyboardEvent, atual: number) => {
    const ordem = abas.map((a) => a.id);
    const alvo = { ArrowLeft: atual - 1, ArrowRight: atual + 1, Home: 0, End: ordem.length - 1 }[e.key];
    if (alvo === undefined) return;
    e.preventDefault();
    const nova = ordem[(alvo + ordem.length) % ordem.length]!;
    setAba(nova);
    abaRefs.current[nova]?.focus();
  };
  const abas: { id: Aba; nome: string }[] = [
    { id: 'foco', nome: 'Foco' },
    { id: 'uso', nome: 'Uso do solo' },
    { id: 'car', nome: nImoveis ? `CAR · ${nImoveis}` : 'CAR' },
  ];
  const municipio = place ?? (estimated && estimated !== 'pending' ? `${estimated.nome}${estimated.uf ? `/${estimated.uf}` : ''}` : null);
  const extras = [
    fire.frp != null ? `FRP ${formatDecimal(fire.frp, 1)} MW` : null,
    fire.diasSemChuva != null ? `${formatInt(fire.diasSemChuva)} ${fire.diasSemChuva === 1 ? 'dia' : 'dias'} sem chuva` : null,
    fire.precipitacao != null ? `${formatDecimal(fire.precipitacao, 1)} mm de chuva` : null,
  ].filter(Boolean);

  return (
    // A ficha muda de altura ao trocar de aba ou quando o terreno chega: o mapa reenquadra (celular).
    <div className="w-72 max-w-full text-xs" data-reenquadrar-ao-crescer="" data-ficha-foco="">
      <Titulo color={color} />
      {t.atende && (
        // Presa ao topo da ficha: se a aba alta foi rolada, as abas continuam à mão.
        <div role="tablist" aria-label="Informações do foco" className="sticky top-0 z-10 mb-2 flex gap-1 rounded-lg bg-ink-800 p-0.5 shadow-[0_-8px_0_0_var(--color-ink-900)]">
          {abas.map((a, k) => (
            <button
              key={a.id}
              ref={(el) => {
                abaRefs.current[a.id] = el;
              }}
              type="button"
              role="tab"
              id={`${id}-aba-${a.id}`}
              aria-selected={aba === a.id}
              aria-controls={aba === a.id ? `${id}-painel` : undefined}
              tabIndex={aba === a.id ? 0 : -1}
              onKeyDown={(e) => teclar(e, k)}
              className={`min-h-8 flex-1 rounded-md px-1.5 text-[11.5px] font-medium ${aba === a.id ? 'bg-ink-600 text-white' : 'text-ink-300 hover:text-white'}`}
              onClick={() => setAba(a.id)}
            >
              {a.nome}
            </button>
          ))}
        </div>
      )}
      <div
        role={t.atende ? 'tabpanel' : undefined}
        id={`${id}-painel`}
        tabIndex={t.atende ? 0 : undefined}
        aria-labelledby={t.atende ? `${id}-aba-${aba}` : undefined}
      >
        {(aba === 'foco' || !t.atende) && (
          <>
            <p className="text-sm text-ink-100">
              {municipio ?? (estimated === 'pending' ? <span className="text-ink-400">estimando o município…</span> : 'Município não identificado')}
              {!place && municipio && <span className="text-[11px] text-ink-400"> (estimado)</span>}
            </p>
            <p className="mt-0.5 text-ink-300">
              {sateliteCurto(fire.satelite)} · {formatDateTimeBrt(fire.t)} <span className="text-ink-400">(Brasília)</span>
            </p>
            {(risk || extras.length > 0) && (
              <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-ink-300">
                {risk && (
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block size-2 rounded-full" style={{ background: risk.color }} aria-hidden />
                    Risco {risk.label.toLowerCase()}
                  </span>
                )}
                {extras.map((e) => (
                  <span key={e}>{e}</span>
                ))}
              </p>
            )}
            {raios && (
              <p className="mt-1 flex items-center gap-1 text-ink-300" data-testid="raios-do-foco">
                <Zap className="size-3 shrink-0 text-yellow-300" aria-hidden />
                {fraseCurta(raios)}
              </p>
            )}
            <p className="mt-1 text-[11px] text-ink-400">{formatLonLat(fire.lon, fire.lat, 4)}</p>
            <OutrosNoPonto stack={stack} />
            <FonteInpe fire={fire} />
          </>
        )}
        {t.atende && aba === 'uso' && <UsoDoSoloDoFoco t={t} />}
        {t.atende && aba === 'car' && <CarDoFoco t={t} lat={fire.lat} lon={fire.lon} />}
      </div>
      {/* Em todas as abas: a página da denúncia reúne o que as três mostram. */}
      <BotaoDenunciar fire={fire} estimado={estimated} />
    </div>
  );
}

export function renderFirePopup(fire: Fire, stack = 1): ReactNode {
  return <FirePopup fire={fire} stack={stack} />;
}
